'use client'

import { useBackdropClose } from '@trustoo/components/modal/hooks/useBackdropClose'
import { useCancelToClose } from '@trustoo/components/modal/hooks/useCancelToClose'
import { useEffect, useRef } from 'react'

interface UsePlainDialogParams {
    open: boolean
    onClose: () => void
    closeOnBackdropClick?: boolean
}

/**
 * Behaviour for the non-animated `Dialog.Root`: syncs the `open` prop with the native
 * `showModal()` / `close()` API (restoring scroll on close), routes Escape through `onClose`, and
 * exposes the backdrop-click handler. Keeps `PlainDialog` purely presentational.
 */
export const usePlainDialog = ({
    open,
    onClose,
    closeOnBackdropClick = false,
}: UsePlainDialogParams) => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const scrollPositionRef = useRef(0)

    // Sync the `open` prop with the native showModal/close API, restoring scroll on close.
    useEffect(() => {
        const dialog = dialogRef.current
        if (!dialog) return

        if (open && !dialog.open) {
            scrollPositionRef.current = window.scrollY
            dialog.showModal()
        } else if (!open && dialog.open) {
            dialog.close()
            window.scrollTo(0, scrollPositionRef.current)
        }
    }, [open])

    useCancelToClose(dialogRef, onClose)

    const onBackdropClick = useBackdropClose(dialogRef, onClose, closeOnBackdropClick)

    return { dialogRef, onBackdropClick }
}
